import type { UseFormReturn } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderTemplateChips } from "web_myplann/Reminders/Components/ReminderTemplateChips";
import type { ReminderFormModel } from "web_myplann/Reminders/Forms/reminderFormSchema";
import { useReminderRecurrenceOptions } from "web_myplann/Reminders/Hooks/useReminderRecurrenceOptions";
import { useReminderTemplateSelection } from "web_myplann/Reminders/Hooks/useReminderTemplateSelection";
import type { ReminderTemplate } from "web_myplann/Reminders/Types/ReminderTypes";
import { getReminderMinDate } from "web_myplann/Reminders/Utils/reminderMinDate";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DEFAULT_DATE_FORMAT } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	form: UseFormReturn<ReminderFormModel>;
	templates: ReminderTemplate[];
};

export const ReminderFormFields = ({ form, templates }: Props) => {
	const { t } = useResource();
	const { control, watch } = form;

	const selectedTypeKey = watch("typeKey");

	const minDate = getReminderMinDate();
	const recurrenceOptions = useReminderRecurrenceOptions();

	const { selectTemplate, markAsCustom } = useReminderTemplateSelection(
		form,
		templates,
	);

	return (
		<>
			<ReminderTemplateChips
				templates={templates}
				selectedTypeKey={selectedTypeKey}
				onSelect={selectTemplate}
			/>

			<FlatRowFormInput
				control={control}
				name="name"
				label={t(Resources.Reminders.Form.nameLabel)}
				placeholder={t(Resources.Reminders.Form.namePlaceholder)}
				onValueChange={markAsCustom}
			/>

			<FlatRowFormDatePicker
				control={control}
				name="date"
				label={t(Resources.Reminders.Form.dateRowLabel)}
				placeholder={t(Resources.Reminders.Form.datePlaceholder)}
				minDate={minDate}
				views={[DateView.Year, DateView.Month, DateView.Day]}
				displayFormat={DEFAULT_DATE_FORMAT}
			/>

			<FlatRowFormSelect
				control={control}
				name="recurrence"
				label={t(Resources.Reminders.Form.recurrenceLabel)}
				options={recurrenceOptions}
				drawerTitle={t(Resources.Reminders.Form.recurrenceLabel)}
				isLast
				onValueChange={markAsCustom}
			/>
		</>
	);
};
